import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Scale, Wallet, DollarSign, Loader2 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

// Lazy load the pages
const FinancialDuesPage = lazy(() => import('./FinancialDuesPage'));
const ExpensesPage = lazy(() => import('./ExpensesPage'));

const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-64">
    <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
  </div>
);

const FinancialsPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get initial tab from URL hash or default to 'dues'
  const getInitialTab = () => {
    const hash = location.hash.replace('#', '');
    return hash === 'expenses' ? 'expenses' : 'dues';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab);

  // Update URL hash when tab changes
  const handleTabChange = (value) => {
    setActiveTab(value);
    navigate(`#${value}`, { replace: true });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
          <DollarSign className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-stone-800">{t('financials') || 'المالية'}</h1>
          <p className="text-stone-500 text-sm">{t('financialsDesc') || 'إدارة المستحقات والمصروفات'}</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6 bg-stone-100 p-1 rounded-lg h-14">
          <TabsTrigger 
            value="dues" 
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2 py-3 text-base"
          >
            <Scale className="w-5 h-5" />
            {t('financialDues') || 'المستحقات المالية'}
          </TabsTrigger>
          <TabsTrigger 
            value="expenses" 
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2 py-3 text-base"
          >
            <Wallet className="w-5 h-5" />
            {t('expenses') || 'المصروفات'}
          </TabsTrigger>
        </TabsList>

        <Suspense fallback={<LoadingSpinner />}>
          <TabsContent value="dues" className="mt-0">
            <FinancialDuesPage />
          </TabsContent>

          <TabsContent value="expenses" className="mt-0">
            <ExpensesPage />
          </TabsContent>
        </Suspense>
      </Tabs>
    </div>
  );
};

export default FinancialsPage;
